import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import PhilipsLogo from "../../../assets/icons/philips-logo.svg";
import FallbackImg from "../../../components/FallbackImg";
import ApiService from "../../../components/Api_services";
import { LanguageProvider, useLanguage } from "../LanguageContext";
import { Globe, Check } from "lucide-react";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";
import "../AfterSalesService.css";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ch", label: "中文" },
  { code: "my", label: "Bahasa Malaysia" },
];

const AfterSalesLayout = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLanguage } = useLanguage();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [registerStatus, setRegisterStatus] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("as-aftersales-body");
    return () => document.body.classList.remove("as-aftersales-body");
  }, []);

  // Pages that should NOT run the register status check
  const skipValidationPaths = [
    "/aftersalesservice",
    "/aftersalesservice/registerserialno",
    "/aftersalesservice/registerserialnobrunei",
    "/aftersalesservice/registerserialnosingle",
    "/aftersalesservice/newinstallation",
  ];

  const shouldSkip = skipValidationPaths.some(
    (path) => location.pathname === path,
  );

  useEffect(() => {
    if (shouldSkip) return;

    const fetchRegisterStatus = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.checkRegisterStatus(
          serialNo,
          serialType,
        );

        if (response) {
          setRegisterStatus(response.status);
        }
      } catch (error) {
        console.error("Error fetching register status:", error);
      }
    };

    fetchRegisterStatus();
  }, [serialNoForApi, serialType, shouldSkip]);

  useEffect(() => {
    if (registerStatus === false) {
      navigate(`/aftersalesservice${buildAfterSalesQuery(searchParams)}`);
    }
  }, [registerStatus]);

  const isNewInstallation =
    location.pathname === "/aftersalesservice/newinstallation";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="as-aftersales">
      {isNewInstallation ? (
        <>{children}</>
      ) : (
        <>
          <div className="as-aftersales-top">
            <FallbackImg src={PhilipsLogo} alt="philipslogo" />

            {/* Language Dropdown */}
            <div className="as-aftersales-lang-switcher" ref={dropdownRef}>
              <button
                className="as-lang-globe-btn"
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-label="Select language"
              >
                <Globe size={20} />
              </button>
              {dropdownOpen && (
                <div className="as-lang-dropdown">
                  {LANGUAGES.map(({ code, label }) => (
                    <div
                      key={code}
                      className={`as-lang-option ${lang === code ? "as-active" : ""}`}
                      onClick={() => {
                        setLanguage(code);
                        setDropdownOpen(false);
                      }}
                    >
                      <span>{label}</span>
                      {lang === code && <Check size={16} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="as-aftersales-bottom">{children}</div>
        </>
      )}
    </div>
  );
};

export default AfterSalesLayout;
